"use client"

import { useState, useEffect } from "react"
import { apiService } from "../../services/apiService"

const UpcomingEvents = () => {
  const [events, setEvents] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    date: new Date().toISOString().split("T")[0],
    image: null
  })
  const [imagePreview, setImagePreview] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [successMessage, setSuccessMessage] = useState("")
  const [formErrors, setFormErrors] = useState({})

  useEffect(() => {
    loadEvents()
  }, [])

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("")
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])

  const loadEvents = async () => {
    try {
      setIsLoading(true)
      setError("")
      const response = await apiService.getEvents()
      console.log('Events response:', response)
      setEvents(response)
    } catch (error) {
      console.error('Error loading events:', error)
      setError(error.message || 'Failed to load events')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })
    // Clear error when user types
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: "",
      })
    }
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setFormErrors({
          ...formErrors,
          image: "Image size should not exceed 5MB"
        })
        return
      }
      // Validate file type
      if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
        setFormErrors({
          ...formErrors,
          image: "Only JPG, JPEG and PNG files are allowed"
        })
        return
      }
      setFormData({
        ...formData,
        image: file
      })
      const imageUrl = URL.createObjectURL(file)
      setImagePreview(imageUrl)
      // Clear error if validation passes
      if (formErrors.image) {
        setFormErrors({
          ...formErrors,
          image: ""
        })
      }
    }
  }

  const validateForm = () => {
    const errors = {}
    if (!formData.title.trim()) {
      errors.title = "Title is required"
    }
    if (!formData.description.trim()) {
      errors.description = "Description is required"
    }
    if (!formData.date) {
      errors.date = "Date is required"
    }
    if (!formData.image) {
      errors.image = "Image is required"
    }
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) {
      return
    }
    try {
      setIsLoading(true)
      setError("")
      await apiService.createEvent(formData)
      setSuccessMessage('Event created successfully!')
      setFormData({
        title: "",
        description: "",
        date: new Date().toISOString().split("T")[0],
        image: null
      })
      setImagePreview(null)
      setShowForm(false)
      await loadEvents()
    } catch (error) {
      console.error('Error creating event:', error)
      setError(error.message || 'Failed to create event')
    } finally {
      setIsLoading(false)
    }
  }

  const deleteEvent = async (id) => {
    if (window.confirm("Are you sure you want to delete this event?")) {
      try {
        setIsLoading(true)
        await apiService.deleteEvent(id)
        await loadEvents()
        setSuccessMessage('Event deleted successfully!')
      } catch (error) {
        console.error("Error deleting event:", error)
        setError(error.message || "Failed to delete event")
      } finally {
        setIsLoading(false)
      }
    }
  }

  return (
    <div className="admin-section">
      <div className="action-buttons">
        <button className="btn" onClick={() => setShowForm(!showForm)} disabled={isLoading}>
          {showForm ? "Cancel" : "Add New Event"}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {showForm && (
        <div className="form-container">
          <h2 className="form-title">Add New Event</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="title">Title</label>
              <input
                type="text"
                id="title"
                name="title"
                className={`form-control ${formErrors.title ? "error" : ""}`}
                value={formData.title}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
              {formErrors.title && <div className="error-message">{formErrors.title}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                className={`form-control ${formErrors.description ? "error" : ""}`}
                value={formData.description}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
              {formErrors.description && <div className="error-message">{formErrors.description}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="date">Date</label>
              <input
                type="date"
                id="date"
                name="date"
                className={`form-control ${formErrors.date ? "error" : ""}`}
                value={formData.date}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
              {formErrors.date && <div className="error-message">{formErrors.date}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="image">Image <span className="required">*</span></label>
              <div className={`image-upload-container ${formErrors.image ? "error" : ""}`}>
                <input
                  type="file"
                  id="image"
                  accept="image/jpeg,image/png,image/jpg"
                  onChange={handleImageChange}
                  className="image-input"
                  disabled={isLoading}
                  required
                />
                <div className="image-upload-placeholder">
                  <i className="material-icons">image</i>
                  <p>Click to upload image or drag and drop</p>
                  <span>PNG, JPG or JPEG (max. 5MB)</span>
                </div>
              </div>
              {formErrors.image && <div className="error-message">{formErrors.image}</div>}
              {imagePreview && (
                <div className="image-preview">
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    style={{
                      maxWidth: '120px',
                      maxHeight: '80px',
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                  <button 
                    type="button" 
                    className="remove-image-btn"
                    onClick={() => {
                      setImagePreview(null)
                      setFormData({...formData, image: null})
                      setFormErrors({
                        ...formErrors,
                        image: "Image is required"
                      })
                    }}
                    disabled={isLoading}
                  >
                    <i className="material-icons">close</i>
                    Remove Image
                  </button>
                </div>
              )}
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)} disabled={isLoading}>
                Cancel
              </button>
              <button type="submit" className="btn" disabled={isLoading}>
                {isLoading ? "Saving..." : "Add Event"}
              </button>
            </div>
          </form>
        </div>
      )}

      <h2 className="section-title">Upcoming Events</h2>

      <div className="events-list">
        {isLoading ? (
          <div className="loading">Loading events...</div>
        ) : events.length === 0 ? (
          <p>No events available</p>
        ) : (
          events.map((event) => (
            <div className="event-item" key={event.id}>
              <div className="event-content">
                <div className="event-header">
                  <h3>{event.title}</h3>
                  <small>{new Date(event.date).toLocaleDateString()}</small>
                </div>
                <div className="event-body">
                  <div className="event-image">
                    {event.image ? (
                      <img 
                        src={`${event.image}`} 
                        alt={event.title} 
                        
                        style={{
                          maxWidth: '120px',
                          maxHeight: '80px',
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <div style={{ width: '120px', height: '80px', backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span>No Image</span>
                      </div>
                    )}
                  </div>
                  <div className="event-details">
                    <p>{event.description}</p>
                  </div>
                </div>
              </div>
              <div className="event-actions">
                <button 
                  className="action-btn delete-btn"
                  onClick={() => deleteEvent(event.id)}
                  disabled={isLoading}
                >
                  <i className="material-icons">delete</i>
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

export default UpcomingEvents
